import { db } from '@database/db';
import { photoDB, PhotoBlobRecord } from '@database/photo-db';

/**
 * Migre les blobs de photos de atlasDB vers atlasPhotosDB
 * 
 * Cette migration :
 * 1. Copie tous les blobs de db.photo_blobs vers photoDB.photo_blobs
 * 2. Supprime les blobs de l'ancienne DB
 * 3. Gère les erreurs et permet de reprendre si interrompu
 * 
 * @returns Statistiques de migration
 */
export async function migratePhotoBlobs(): Promise<{
	migrated: number;
	failed: number;
	skipped: number;
	duration: number;
}> {
	const startTime = Date.now();
	let migrated = 0;
	let failed = 0;
	let skipped = 0;

	console.log('🔄 Démarrage migration photo_blobs vers DB dédiée...');

	try {
		// 1. Vérifier si la migration a déjà été faite
		const existingCount = await photoDB.photo_blobs.count();
		if (existingCount > 0) {
			console.log(`⚠️ ${existingCount} blobs déjà présents dans photoDB, migration déjà effectuée`);
			return { migrated: 0, failed: 0, skipped: existingCount, duration: Date.now() - startTime };
		}

		// 2. Récupérer tous les blobs de l'ancienne DB
		const oldBlobs = await db.photo_blobs.toArray();
		console.log(`📦 ${oldBlobs.length} blobs à migrer`);

		if (oldBlobs.length === 0) {
			console.log('✅ Aucun blob à migrer');
			return { migrated: 0, failed: 0, skipped: 0, duration: Date.now() - startTime };
		}

		// 3. Migrer par lots de 50 pour éviter les timeouts
		const BATCH_SIZE = 50;
		for (let i = 0; i < oldBlobs.length; i += BATCH_SIZE) {
			const batch = oldBlobs.slice(i, i + BATCH_SIZE);
			
			try {
				// Copier vers la nouvelle DB
				await photoDB.photo_blobs.bulkAdd(batch);
				migrated += batch.length;

				console.log(`✅ Migré ${migrated}/${oldBlobs.length} blobs (${Math.round(migrated / oldBlobs.length * 100)}%)`);

			} catch (error) {
				console.error(`❌ Erreur migration batch ${i}-${i + batch.length}:`, error);
				failed += batch.length;
			}

			// Petite pause pour ne pas bloquer l'UI
			await new Promise(resolve => setTimeout(resolve, 10));
		}

		// 4. Nettoyer l'ancienne table (on la vide mais on ne la supprime pas pour rétrocompatibilité)
		if (migrated > 0) {
			console.log('🧹 Nettoyage de l\'ancienne table photo_blobs...');
			await db.photo_blobs.clear();
			console.log('✅ Ancienne table vidée');
		}

		const duration = Date.now() - startTime;
		console.log(`✅ Migration terminée: ${migrated} migrés, ${failed} échecs (${duration}ms)`);

		return { migrated, failed, skipped, duration };

	} catch (error) {
		console.error('❌ Erreur fatale durant la migration:', error);
		throw error;
	}
}

/**
 * Vérifie si la migration a été effectuée
 */
export async function isMigrationComplete(): Promise<boolean> {
	try {
		const photoDBCount = await photoDB.photo_blobs.count();
		const oldDBCount = await db.photo_blobs.count();

		// Migration complète si photoDB a des données et oldDB est vide
		return photoDBCount > 0 && oldDBCount === 0;
	} catch (error) {
		console.error('Erreur vérification migration:', error);
		return false;
	}
}

/**
 * Annule la migration (pour tests/debug)
 */
export async function rollbackMigration(): Promise<void> {
	console.warn('⚠️ Rollback de la migration photo_blobs');
	
	try {
		const blobs = await photoDB.photo_blobs.toArray();
		
		if (blobs.length > 0) {
			await db.photo_blobs.bulkAdd(blobs);
			await photoDB.photo_blobs.clear();
			console.log(`✅ ${blobs.length} blobs restaurés dans l'ancienne DB`);
		}
	} catch (error) {
		console.error('❌ Erreur durant le rollback:', error);
		throw error;
	}
}
